import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import Commons from '../../../../../Services/Common/Common';
import Global from '../../../../../Services/Common/Global';
import Improvement from './Improvement';
import { useSearch1, useSave1 } from './Improvement_QUERY';
import { useSearch1 as useSafetyInfoSearch1 } from '../Step01_SafetyInfo/SafetyInfo_QUERY';
import { useWaitDashboard1 } from '../Step00_Wait/Wait_QUERY';
import {
  BuildImprovementReportRows,
  FormatGaesunComDateTime,
  IMPROVEMENT_SAVE_STEP_SEQ,
} from './Improvement_Schema';

/**
 * 개선보고서 기능단 — GetRisCustom02R1 + GetRisCustom01R1 병합,
 * BuildImprovementReportRows(개선예정일·완료일·개선 후 빈도·강도·개선 후 위험성 점수 모두 있는 행), SaveRisCustom03U1
 */
function ImprovementF({
  onCancel,
  onSaved,
  assessmentTitle = '',
  assessmentRsCd = null,
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const searchInfo = useSelector((s) => s.SearchInfo);

  const { data: waitDashboardData } = useWaitDashboard1({
    custCd: searchInfo?.custCd,
    rsCd: assessmentRsCd,
    titleOverride: assessmentTitle,
    enabled: Number(assessmentRsCd) > 0,
  });

  const workflowStepsFromApi = useMemo(() => {
    const live = waitDashboardData?.steps;
    if (!Array.isArray(live) || live.length === 0) return [];
    return live.map((s, i) => {
      const id = s.routeStepId || s.stepId || `step-${i}`;
      return {
        stepId: id,
        routeStepId: s.routeStepId || s.stepId,
        title: String(s.title ?? s.stepNm ?? `단계 ${i + 1}`),
        status: s.status,
      };
    });
  }, [waitDashboardData?.steps]);

  const {
    data: safety01Raw,
    isFetching: safetyFetching,
    refetch: RefetchSafety01,
  } = useSafetyInfoSearch1({
    rsCd: assessmentRsCd,
    enabled: Number(assessmentRsCd) > 0,
  });

  const {
    data: search02Raw,
    isFetching: risk02Fetching,
    refetch: RefetchSearch02,
  } = useSearch1({
    rsCd: assessmentRsCd,
    enabled: Number(assessmentRsCd) > 0,
  });

  const rows = useMemo(
    () => BuildImprovementReportRows(safety01Raw, search02Raw),
    [safety01Raw, search02Raw]
  );

  const loading = safetyFetching || risk02Fetching;

  const { runAsync: Save1Async } = useSave1();
  const [excelDownloading, setExcelDownloading] = useState(false);

  const ExcelDownload1 = useCallback(async () => {
    if (!Array.isArray(rows) || rows.length === 0) {
      dispatch(
        showSnackbar({
          message: '다운로드할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    setExcelDownloading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('개선보고서', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      const headers = [
        '공정명',
        '위험발생상황및결과',
        '개선 전 이미지',
        '감소대책대응',
        '개선 후 이미지',
        '완료일자',
      ];

      const headerRow = worksheet.getRow(1);
      headers.forEach((header, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = header;
        cell.font = { name: '맑은 고딕', size: 10, bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE8F5E9' },
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF808080' } },
          left: { style: 'thin', color: { argb: 'FF808080' } },
          bottom: { style: 'thin', color: { argb: 'FF808080' } },
          right: { style: 'thin', color: { argb: 'FF808080' } },
        };
      });
      headerRow.height = 22;
      headerRow.commit();

      const Str = (v) => (v == null ? '' : String(v));
      const ResolveImageExtension = (contentType = '', url = '') => {
        const ct = String(contentType || '').toLowerCase();
        if (ct.includes('png')) return 'png';
        if (ct.includes('gif')) return 'gif';
        if (ct.includes('webp')) return 'png';
        if (ct.includes('jpeg') || ct.includes('jpg')) return 'jpeg';
        const u = String(url || '').toLowerCase();
        if (u.endsWith('.png')) return 'png';
        if (u.endsWith('.gif')) return 'gif';
        return 'jpeg';
      };
      const ParseDataUrl = (dataUrl) => {
        const m = String(dataUrl || '').match(/^data:([^;]+);base64,(.+)$/i);
        if (!m) return null;
        const contentType = m[1] || '';
        const base64 = m[2] || '';
        const binary = atob(base64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
        return {
          buffer: bytes.buffer,
          extension: ResolveImageExtension(contentType, ''),
        };
      };
      const ToDownloadFilePath = (rawPath) => {
        const raw = String(rawPath ?? '').trim();
        if (!raw) return '';
        if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
        let filePath = raw;
        // 절대 URL/프로토콜 상대 URL(//)은 pathname만 추출해 /files/ 이후 경로로 정규화
        if (/^https?:\/\//i.test(filePath) || filePath.startsWith('//')) {
          try {
            const u = new URL(
              filePath.startsWith('//')
                ? `${window.location.protocol}${filePath}`
                : filePath
            );
            filePath = `${u.pathname}${u.search ?? ''}`;
          } catch {
            // URL 파싱 실패 시 원문 사용
          }
        }
        const basePrefix = `${String(Global.file.useUrl ?? '').replace(/\/?$/, '/')}files/`;
        if (basePrefix && filePath.includes(basePrefix)) {
          filePath = decodeURIComponent(filePath.replace(basePrefix, ''));
        }
        filePath = filePath
          .replace(/^\/?files\//i, '')
          .replace(/\\/g, '/')
          .replace(/[?#].*$/, '');
        return filePath;
      };
      const FetchImageBuffer = async (rawPath) => {
        const filePath = ToDownloadFilePath(rawPath);
        if (!filePath) throw new Error('이미지 경로가 없습니다.');
        if (filePath.startsWith('data:')) {
          const parsed = ParseDataUrl(filePath);
          if (!parsed) throw new Error('data URL 파싱 실패');
          return parsed;
        }
        const fileName = decodeURIComponent(
          String(filePath).replace(/[?#].*$/, '').split('/').pop() || 'image.jpg'
        );
        const res = await Commons.Axios(
          'fileDown',
          Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
          { FileName: fileName, Url: Commons.EncodeUrl(filePath) }
        );
        if (!res?.data) throw new Error('파일 다운로드 응답이 비어 있습니다.');
        const contentType = res?.headers?.['content-type'] ?? '';
        const arrayBuffer = res.data;
        return {
          buffer: arrayBuffer,
          extension: ResolveImageExtension(contentType, fileName),
        };
      };
      const InsertImageOrFallback = async (item, rowNo, cellIndex, rawPath) => {
        if (!String(rawPath ?? '').trim()) {
          worksheet.getCell(rowNo, cellIndex).value = Str(rawPath);
          return;
        }
        try {
          const { buffer, extension } = await FetchImageBuffer(rawPath);
          const imageId = workbook.addImage({
            buffer,
            extension,
          });
          worksheet.addImage(imageId, {
            tl: { col: cellIndex - 1 + 0.06, row: rowNo - 1 + 0.08 },
            br: { col: cellIndex - 1 + 0.94, row: rowNo - 1 + 0.92 },
            editAs: 'oneCell',
          });
        } catch {
          worksheet.getCell(rowNo, cellIndex).value = Str(rawPath);
        }
      };

      for (let index = 0; index < rows.length; index += 1) {
        const item = rows[index];
        const rowNo = 2 + index;
        const row = worksheet.getRow(rowNo);
        row.getCell(1).value = Str(item.processNm);
        row.getCell(2).value = Str(item.categoryComment);
        row.getCell(4).value = Str(item.reductionMeasures);
        const doneTxt = FormatGaesunComDateTime(item.gaesunComDate);
        row.getCell(6).value = doneTxt === '—' ? '' : doneTxt;
        row.height = 78;

        for (let c = 1; c <= 6; c += 1) {
          const cell = row.getCell(c);
          cell.font = { name: '맑은 고딕', size: 10 };
          cell.alignment = {
            vertical: 'top',
            horizontal: 'left',
            wrapText: true,
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF808080' } },
            left: { style: 'thin', color: { argb: 'FF808080' } },
            bottom: { style: 'thin', color: { argb: 'FF808080' } },
            right: { style: 'thin', color: { argb: 'FF808080' } },
          };
        }
        row.commit();
        // 이미지 삽입 실패 시 원본 경로 텍스트로 fallback
        await InsertImageOrFallback(item, rowNo, 3, item.riskImageFilePath);
        await InsertImageOrFallback(item, rowNo, 5, item.afterRiskImageFilePath);
      }

      worksheet.columns = [
        { width: 18 },
        { width: 36 },
        { width: 26 },
        { width: 36 },
        { width: 26 },
        { width: 18 },
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const ts = dayjs().format('YYYYMMDDHHmmss');
      const fileName = `개선보고서_${ts}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);

      dispatch(
        showSnackbar({
          message: '엑셀 파일을 저장했습니다.',
          type: 'success',
        })
      );
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message ?? '엑셀 다운로드 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      setExcelDownloading(false);
    }
  }, [dispatch, rows]);

  const SaveLogic1 = async () => {
    const rsCd = Number(assessmentRsCd);
    if (!Number.isFinite(rsCd) || rsCd <= 0) {
      dispatch(
        showSnackbar({
          message: '위험성평가 코드(RS_CD)가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    try {
      const res = await Save1Async({
        rsCd,
        stepSeq: IMPROVEMENT_SAVE_STEP_SEQ,
        userInfo,
      });
      const ok = res?.IsSuccess ?? res?.isSuccess;
      const msg = res?.Message ?? res?.message;
      if (ok === false) {
        dispatch(
          showSnackbar({
            message: msg || '저장 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        return;
      }
      dispatch(
        showSnackbar({
          message: msg || '저장되었습니다.',
          type: 'success',
        })
      );
      await RefetchSafety01();
      await RefetchSearch02();
      onSaved?.();
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    }
  };

  return (
    <Improvement
      assessmentTitle={assessmentTitle}
      assessmentRsCd={assessmentRsCd}
      rows={rows}
      loading={loading}
      workflowSteps={workflowStepsFromApi}
      onBack={onCancel}
      onSaveAndNext={SaveLogic1}
      onExcelDownload={ExcelDownload1}
      excelDownloading={excelDownloading}
      excelDownloadDisabled={rows.length === 0}
    />
  );
}

export default ImprovementF;
